import LZString from 'lz-string';

const EXPIRATION_MS = 30 * 60 * 1000; // 30mins

/* Legacy secret for reading old data. 
   New data relies on compression for "obfuscation" (it makes it binary-like and unreadable). */
const LIGHT_OBFUSCATION_SECRET = 'planjsonexport$K8z!Vm2#Xq7FdLbN0Rt';

export const LAST_SAVE_THRESHOLD_MS = 30 * 1000; // 30 seconds
export const LOCAL_STORAGE_KEY = 'plan';

interface ExpiringStorage<T> {
  data: T;
  timestamp: number;
}

// --- LEGACY FALLBACKS (Do not remove until all users have migrated) ---

const lightDeobfuscate = (data: string): string => {
  try {
    const decoded = atob(data); // Base64 decode FIRST
    const unxorred = Array.from(decoded)
      .map((char, i) => String.fromCharCode(char.charCodeAt(0) ^ LIGHT_OBFUSCATION_SECRET.charCodeAt(i % LIGHT_OBFUSCATION_SECRET.length)))
      .join('');
    return unxorred;
  } catch (e) {
    // This is expected if we try to deobfuscate compressed data, so we silence the warning usually
    return '';
  }
};

// ----------------------------------------------------------------------

export const saveWithExpiration = <T>(key: string, data: T): void => {
  const payload: ExpiringStorage<T> = {
    data,
    timestamp: Date.now(),
  };

  try {
    const stringified = JSON.stringify(payload);
    
    // NEW STRATEGY: Compress to UTF16
    const compressed = LZString.compressToUTF16(stringified);
    
    localStorage.setItem(key, compressed);
  } catch (e) {
    if (e instanceof Error && e.name === 'QuotaExceededError') {
      console.error(`Save Failed: LocalStorage is full (${key}). Consider clearing data.`);
    } else {
      console.warn(`Failed to save ${key}`, e);
    }
  }
};

export const loadWithExpiration = <T>(key: string): T | null => {
  const storedItem = localStorage.getItem(key);
  if (!storedItem) return null;

  let parsed: ExpiringStorage<T> | null = null;

  // 1. Try Strategy A: Decompression (New Format)
  try {
    const decompressed = LZString.decompressFromUTF16(storedItem);
    
    // If decompressed is null, it means the string wasn't valid LZ-compressed data (likely Legacy)
    if (decompressed) {
      parsed = JSON.parse(decompressed);
    }
  } catch (e) {
    // JSON parse error on decompressed data? Ignore and try legacy.
  }

  // 2. Try Strategy B: Legacy Deobfuscation (Old Format)
  // Only run if Strategy A failed
  if (!parsed) {
    const rawLegacy = lightDeobfuscate(storedItem);
    if (rawLegacy) {
      try {
        parsed = JSON.parse(rawLegacy);
      } catch (e) {
        console.warn(`Failed to parse legacy data for ${key}`);
      }
    }
  }

  // 3. Final Validation
  if (!parsed) {
    console.warn(`Could not load ${key} (tried both Compressed and Legacy formats). Clearing.`);
    localStorage.removeItem(key);
    return null;
  }

  if (Date.now() - parsed.timestamp > EXPIRATION_MS) {
    // console.log('Plan expired');
    localStorage.removeItem(key);
    return null;
  }

  return parsed.data;
};

export const clearStorageKey = (key: string): void => {
  localStorage.removeItem(key);
};